import React, { forwardRef, useContext, useRef, useState } from 'react';
import classnames from 'clsx';
import styles from '../button.scss';
import { TestIds } from '../../../../constants';
import StylesContext from '../../../StylesContext';
import type { TextSizeBlockType } from './utils/utils';
import {
  preventBubblingUp,
  textSizeBlockTypes,
  getActiveTextSize,
  getStateWithNewTextSize,
} from './utils/utils';
import type { ExtendedToolbarChildrenProps } from '../../types';
import { useClickOutside } from '../../../../../../providers/useClickOutside/useClickOutside';
import Tooltip from '../Tooltip';
import { useActionTranslations } from '../../../useActionTranslations';

export const textSizeButtons: Record<TextSizeBlockType, string> = {
  unstyled: 'Normal Text',
  'header-two': 'Title',
  'header-three': 'Subtitle',
};

const InlineTextSizeButton = forwardRef<
  HTMLButtonElement,
  ExtendedToolbarChildrenProps
>((props, ref) => {
  const { setEditorState, getEditorState, rootId, translations } = props;
  const [showOptions, setShowOptions] = useState(false);
  const { toolbarNormalTextAction, toolbarTitleAction, toolbarSubtitleAction } =
    useActionTranslations(translations);

  const titleTextSizeRef = useRef<HTMLButtonElement>(null);
  const subtitleTextSizeRef = useRef<HTMLButtonElement>(null);
  const normalTextSizeRef = useRef<HTMLButtonElement>(null);

  const textSizeActions: Record<TextSizeBlockType, string> = {
    unstyled: toolbarNormalTextAction,
    'header-two': toolbarTitleAction,
    'header-three': toolbarSubtitleAction,
  };

  const textSizesSelected: Record<TextSizeBlockType, string> = {
    unstyled: translations.textSizeNormalTextSelected,
    'header-two': translations.textSizeTitleSelected,
    'header-three': translations.textSizeSubtitleSelected,
  };

  const hideOptions = () => {
    setShowOptions(false);
  };

  const stylesContext = useContext(StylesContext);

  useClickOutside(
    [
      ref as React.RefObject<HTMLElement>,
      titleTextSizeRef,
      subtitleTextSizeRef,
      normalTextSizeRef,
    ],
    hideOptions,
  );

  const onKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      setShowOptions(false);
      if (ref && 'current' in ref) {
        ref.current?.focus();
      }
    }
  };

  const textSizeRef: Record<
    TextSizeBlockType,
    React.RefObject<HTMLButtonElement>
  > = {
    unstyled: normalTextSizeRef,
    'header-two': titleTextSizeRef,
    'header-three': subtitleTextSizeRef,
  };

  const changeTextSize = (
    e: React.MouseEvent<HTMLOrSVGElement>,
    newTextSize: string,
  ) => {
    preventBubblingUp(e);

    const newState = getStateWithNewTextSize(newTextSize, getEditorState());
    hideOptions();
    setEditorState(newState);
  };

  const onClickHandler = (event: React.MouseEvent<any>) => {
    preventBubblingUp(event);
    setShowOptions(true);
  };

  const activeTextSize = getActiveTextSize(getEditorState());

  return (
    <div className={styles.buttonContainer}>
      <Tooltip
        shouldShow={props.shouldShowTooltips}
        content={translations.textSizeSelected}
      >
        <button
          onMouseDown={e => e.preventDefault()}
          onKeyDown={onKeyDown}
          type="button"
          ref={ref}
          className={classnames(styles.button, styles.buttonTextContent, {
            [styles.hovered]: stylesContext.hovered,
            [styles.focused]: stylesContext.focused,
            [styles.error]: stylesContext.error,
          })}
          onClick={onClickHandler}
          data-testid={TestIds.textSizeButton}
          tabIndex={0}
          aria-haspopup="true"
          aria-expanded={showOptions}
          aria-controls={`textSizeMenu_${rootId}`}
          aria-label={`${translations.textSizeSelected}: ${textSizesSelected[activeTextSize]}`}
        >
          {textSizeButtons[activeTextSize]}
        </button>
      </Tooltip>
      {showOptions && (
        <div
          role="menu"
          className={classnames(
            styles.menuContainer,
            {
              [styles.hovered]: stylesContext.hovered,
              [styles.focused]: stylesContext.focused,
              [styles.error]: stylesContext.error,
            },
            styles.inline,
          )}
          id={`textSizeMenu_${rootId}`}
        >
          {textSizeBlockTypes.map(textSize => (
            <Tooltip
              shouldShow={props.shouldShowTooltips}
              content={textSizeActions[textSize]}
              key={`tooltip_text_size_${textSize}`}
              position="right"
            >
              <button
                onClick={e => changeTextSize(e, textSize)}
                ref={textSizeRef[textSize]}
                className={classnames(
                  styles.buttonTextContent,
                  textSize === activeTextSize && styles.buttonTextSelected,
                )}
                data-testid={`${TestIds.textSizeButton}-${textSize}`}
                role="menuitemradio"
                aria-checked={activeTextSize === textSize}
                onMouseDown={e => e.preventDefault()}
                aria-label={textSizeActions[textSize]}
              >
                {textSizeButtons[textSize]}
              </button>
            </Tooltip>
          ))}
        </div>
      )}
    </div>
  );
});

export default InlineTextSizeButton;
